import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Skeleton, Badge, Button, Card, CardBody, CardHeader, CardTitle } from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { usePolicy, useUpdatePolicy, useDeletePolicy, s3ExtractError, type PolicyEntities } from '@/api/s3';
import { S3ConsoleBreadcrumbs } from '../S3ConsoleBreadcrumbs';
import { Mono } from '../s3-parts';
import { S3FieldList, type Field } from '../S3FieldList';
import { JsonEditor } from '@/components/JsonEditor';

/** Format an ISO date to a short local string; blank when absent. */
function fmtDate(v?: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const totalEntities = (e?: PolicyEntities) =>
  (e?.users.length ?? 0) + (e?.groups.length ?? 0) + (e?.roles.length ?? 0);

/** Read-only view of one policy — metadata, attached principals and JSON document. */
export function PolicyDetail({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const { arn = '' } = useParams<{ arn: string }>();
  // arn arrives URL-encoded in the route param; decode before querying.
  const decodedArn = decodeURIComponent(arn);
  const { data, isLoading } = usePolicy(serviceId, decodedArn);
  const toast = useToast();
  const update = useUpdatePolicy(serviceId);
  const del = useDeletePolicy(serviceId);

  const managed = data?.managed;
  // Only customer-managed policies are editable/deletable; AWS-managed built-ins
  // are read-only.
  const canEdit = !!data && managed === false;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const confirmDelete = () => {
    if (!data) return;
    del.mutate(data.arn, {
      onSuccess: () => {
        toast.success('Policy deleted');
        navigate(`/services/oss/${serviceId}/policies`);
      },
      onError: (e) => {
        toast.error(s3ExtractError(e, 'Failed to delete policy'));
        setConfirmingDelete(false);
      },
    });
  };

  const startEdit = () => {
    setDraft(data?.document ?? '');
    setEditing(true);
  };
  const save = () => {
    if (!data) return;
    try {
      JSON.parse(draft);
    } catch {
      toast.error('Document must be valid JSON');
      return;
    }
    update.mutate(
      { arn: data.arn, document: draft },
      {
        onSuccess: () => {
          toast.success('Policy updated');
          setEditing(false);
        },
        onError: (e) => toast.error(s3ExtractError(e, 'Failed to update policy')),
      },
    );
  };

  const overviewFields: Field[] = [
    {
      label: 'Type',
      value: (
        <Badge tone={managed ? 'info' : 'neutral'} appearance="subtle" size="sm">
          {managed ? 'AWS managed' : 'Customer managed'}
        </Badge>
      ),
    },
    {
      label: 'Attachable',
      value: (
        <Badge tone={data?.is_attachable ? 'success' : 'neutral'} appearance="subtle" size="sm">
          {data?.is_attachable ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    { label: 'Attachments', value: data?.attachment_count ?? 0 },
    ...(data?.policy_id ? [{ label: 'Policy ID', value: data.policy_id, mono: true }] : []),
    { label: 'Created', value: fmtDate(data?.create_date) },
  ];

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <S3ConsoleBreadcrumbs
            trail={[
              { label: 'Policies', slug: 'policies' },
              { label: data?.policy_name ?? 'Policy' },
            ]}
          />
        }
        title={data?.policy_name ?? 'Policy'}
        description={
          managed
            ? 'AWS-managed IAM policy (read-only).'
            : 'Customer-managed IAM policy (read-only).'
        }
        actions={
          canEdit ? (
            <Button
              variant="danger"
              size="sm"
              startIcon={<Trash2 size={14} />}
              onClick={() => setConfirmingDelete(true)}
            >
              Delete
            </Button>
          ) : undefined
        }
      />

      {isLoading ? (
        <Skeleton variant="rect" height={320} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Card flat>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
            </CardHeader>
            <CardBody>
              <S3FieldList fields={overviewFields} />
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>ARN</CardTitle>
            </CardHeader>
            <CardBody>
              <Mono>{data?.arn ?? decodedArn}</Mono>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Attached to</CardTitle>
            </CardHeader>
            <CardBody>
              {totalEntities(data?.entities) === 0 ? (
                <p style={{ color: 'var(--vn-fg-muted)', margin: 0 }}>
                  Not attached to any user, group or role.
                </p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
                  {data?.entities?.users.map((u) => (
                    <Badge key={`u-${u}`} tone="info" appearance="subtle" size="sm">
                      User · {u}
                    </Badge>
                  ))}
                  {data?.entities?.groups.map((g) => (
                    <Badge key={`g-${g}`} tone="info" appearance="subtle" size="sm">
                      Group · {g}
                    </Badge>
                  ))}
                  {data?.entities?.roles.map((r) => (
                    <Badge key={`r-${r}`} tone="info" appearance="subtle" size="sm">
                      Role · {r}
                    </Badge>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader
              actions={
                (canEdit || editing) &&
                (editing ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={update.isPending}
                      onClick={() => setEditing(false)}
                    >
                      Cancel
                    </Button>
                    <Button size="sm" disabled={update.isPending} onClick={save}>
                      {update.isPending ? 'Saving…' : 'Save changes'}
                    </Button>
                  </>
                ) : (
                  <Button variant="secondary" size="sm" startIcon={<Pencil size={14} />} onClick={startEdit}>
                    Edit
                  </Button>
                ))
              }
            >
              <CardTitle>Document</CardTitle>
            </CardHeader>
            <CardBody>
              {/* CodeMirror JSON viewer/editor: read-only unless editing a
                  customer-managed policy. */}
              <JsonEditor
                value={editing ? draft : (data?.document ?? '')}
                onChange={setDraft}
                readOnly={!editing}
                ariaLabel="Policy document"
              />
            </CardBody>
          </Card>
        </div>
      )}

      <ConfirmDialog
        open={confirmingDelete}
        onOpenChange={(o) => !o && setConfirmingDelete(false)}
        title="Delete policy"
        description={`Policy "${data?.policy_name}" will be permanently removed. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={confirmDelete}
      />
    </Container>
  );
}
